import { FilterOperator, SortDirection } from '~/common/generated/apollo-operations'
import { identifyJitsuUser, trackJitsuEvent } from '../../jitsu'

enum SegmentEvent {
  GLOBAL_SEARCH_USED = 'Global Search Used',
  TABLE_FILTERED = 'Table Filtered',
  TABLE_SEARCH_USED = 'Table Search Used',
  TABLE_SORTED = 'Table Sorted',
  TABLE_VISIBLE_COLUMNS_CHANGED = 'Table Visible Columns Changed',
  USER_FAILED_LOG_IN = 'User Failed Login',
  USER_FAILED_SET_PASSWORD = 'User Failed Set Password',
  USER_LOGGED_IN = 'User Logged In',
  USER_LOGGED_OUT = 'User Logged Out',
  USER_SET_PASSWORD = 'User Set Password',
  URL_FORWARDING_USED = 'URL Forwarding (to Stripe) Used',
}

type IdentifyUser = {
  name: string
  email: string | null
  phone: string | null
  organizationName: string
}

export function identifyUser(userId: string, traits: IdentifyUser) {
  identifyJitsuUser(userId, traits)
}

export function track(event: SegmentEvent, properties?: Record<string, unknown>) {
  trackJitsuEvent(event, (properties ?? {}) as Parameters<typeof trackJitsuEvent>[1])
}

/**
 * Tracks when global search is used
 */
export function trackGlobalSearch(properties: { searchTerm: string }) {
  track(SegmentEvent.GLOBAL_SEARCH_USED, properties)
}

/**
 * Tracks when table search is used
 */
export function trackTableSearch(properties: { searchTerm: string; tableName: string }) {
  track(SegmentEvent.TABLE_SEARCH_USED, properties)
}

/**
 * Tracks with table sort is used
 */
export function trackTableSorts(properties: {
  sorts: { columnId: string; direction: SortDirection }[]
  tableName: string
}) {
  track(SegmentEvent.TABLE_SORTED, properties)
}

/**
 * Tracks when table filter is used
 */
export function trackTableFilters(properties: {
  tableName: string
  filters: {
    columnId: string
    operator: FilterOperator
    filterString?: string | null
    filterArray?: string[] | null
  }[]
}) {
  track(SegmentEvent.TABLE_FILTERED, properties)
}

/**
 * Tracks when table visible columns are changed
 */
export function trackTableVisibleColumns(properties: {
  tableName: string
  columnsAdded: string[]
  columnsRemoved: string[]
}) {
  track(SegmentEvent.TABLE_VISIBLE_COLUMNS_CHANGED, properties)
}

/**
 * Tracks when a user logged in
 */
export function trackUserLogIn(properties: { email?: string | null; phone?: string | null }) {
  track(SegmentEvent.USER_LOGGED_IN, properties)
}

/**
 * Tracks when a user failed to log in
 */
export function trackUserFailedLogIn(properties: {
  email?: string | null
  phone?: string | null
  errorMessage?: string
}) {
  track(SegmentEvent.USER_FAILED_LOG_IN, properties)
}

/**
 * Tracks when a user set their password
 */
export function trackUserSetPassword(properties: { email?: string | null; phone?: string | null }) {
  track(SegmentEvent.USER_SET_PASSWORD, properties)
}

/**
 * Tracks when a user failed to set their password
 */
export function trackUserFailedSetPassword(properties: {
  token: string
  email?: string | null
  phone?: string | null
}) {
  track(SegmentEvent.USER_FAILED_SET_PASSWORD, properties)
}

/**
 * Tracks when a user logged out
 */
export function trackUserLogOut(properties: { email?: string | null; phone?: string | null }) {
  track(SegmentEvent.USER_LOGGED_OUT, properties)
}

/**
 * Tracks when a user uses the encrypt/decrypt feature
 */
export function trackUrlForwarding(url: string) {
  track(SegmentEvent.URL_FORWARDING_USED, { url })
}
